import type {
    ResourceTypeHandle,
    Tool,
    ToolHandle,
} from '@toolproof-core/genesis/_contract/_index';

import { extractResourceTypeHandleFromRole } from '@toolproof-core/lib/_pure/workflow-extraction';
import type {
    MutableToolStep,
    MutableToolStepKey,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import { deriveUniformToolStepRingLayout } from '@/workflow/_pure/derivation/toolStepRingLayout';
import type { ToolStepRingLayout } from '@/workflow/_contract/geometry';
import type {
    ResourceTypeAngularInfo,
    StepSpokeExtents,
    WorkflowStateEntriesConfig,
} from '@/workflow/_contract/kernel';

export type ToolStepSlotSnapshot = {
    loopWhatToolStepKeys?: ReadonlySet<MutableToolStepKey>;
    loopWhenToolStepKeys?: ReadonlySet<MutableToolStepKey>;
    branchWhatToolStepKeys?: ReadonlySet<MutableToolStepKey>;
    branchWhenToolStepKeys?: ReadonlySet<MutableToolStepKey>;
};

export type RadialLayoutMetrics = {
    resourceTypeRingRadius: number;
    toolStepRingGap: number;
    toolStepRingRadii: readonly number[];
    toolStepConcentricRingOuterRadius: number;
    maxArityBetweenToolSteps: number;
    workflowStateEntryGap: number;
};

export function validateRadialLayoutConfig(args: {
    toolRingOuterRadius: number;
    resourceTypeRingRadiusMultiplier: number;
    toolStepRingGapMultiplier: number;
}): void {
    if (!Number.isFinite(args.toolRingOuterRadius) || args.toolRingOuterRadius <= 0) {
        throw new Error('Tool ring outer radius must be finite and greater than zero.');
    }
    for (const [name, value] of [
        ['resourceTypeRingRadiusMultiplier', args.resourceTypeRingRadiusMultiplier],
        ['toolStepRingGapMultiplier', args.toolStepRingGapMultiplier],
    ] as const) {
        if (!Number.isFinite(value) || value <= 0) {
            throw new Error(`${name} must be finite and greater than zero.`);
        }
    }
}

export function normalizePlanarDirection(direction: [number, number, number]): [number, number, number] {
    const [x, , z] = direction;
    const length = Math.hypot(x, z);
    if (!Number.isFinite(length) || length <= 1e-8) return [1, 0, 0];
    return [x / length, 0, z / length];
}

export function buildItemOffsetsForSpoke(args: {
    itemCount: number;
    workflowStateEntryGap: number;
}): number[] {
    const { itemCount, workflowStateEntryGap } = args;
    if (itemCount <= 0) return [];
    if (!Number.isFinite(workflowStateEntryGap) || workflowStateEntryGap < 0) {
        throw new Error('Workflow state entry gap must be finite and non-negative.');
    }
    return Array.from({ length: itemCount }, (_, index) => index * workflowStateEntryGap);
}

export function deriveMaximumWorkflowStateEntryArity(args: {
    orderedToolSteps: MutableToolStep[];
    toolMap: ReadonlyMap<ToolHandle, Tool>;
}): number {
    let maximumArity = 0;
    for (const toolStep of args.orderedToolSteps) {
        const tool = args.toolMap.get(toolStep.toolHandle);
        if (!tool) continue;
        for (const roleByName of [tool.roleSpec.inputRoleByName, tool.roleSpec.outputRoleByName]) {
            const countByResourceType = countRoleEntriesByResourceType({
                roleByName: roleByName as Record<string, unknown>,
            });
            for (const count of countByResourceType.values()) {
                maximumArity = Math.max(maximumArity, count);
            }
        }
    }
    return maximumArity;
}

function countRoleEntriesByResourceTypeForToolStep(args: {
    toolStep: MutableToolStep;
    toolMap: ReadonlyMap<ToolHandle, Tool>;
    direction: 'input' | 'output';
}): Map<ResourceTypeHandle, number> {
    const tool = args.toolMap.get(args.toolStep.toolHandle);
    if (!tool) return new Map();
    const roleByName = args.direction === 'input'
        ? tool.roleSpec.inputRoleByName
        : tool.roleSpec.outputRoleByName;
    return countRoleEntriesByResourceType({
        roleByName: roleByName as Record<string, unknown>,
    });
}

export function deriveMaximumAdjacentWorkflowStateEntryArity(args: {
    orderedToolSteps: MutableToolStep[];
    toolMap: ReadonlyMap<ToolHandle, Tool>;
}): number {
    let maximumArity = 0;
    const resourceTypes = new Set<ResourceTypeHandle>();
    const inputsByStep = args.orderedToolSteps.map((toolStep) => {
        const counts = countRoleEntriesByResourceTypeForToolStep({
            toolStep,
            toolMap: args.toolMap,
            direction: 'input',
        });
        counts.forEach((_, resourceTypeHandle) => resourceTypes.add(resourceTypeHandle));
        return counts;
    });
    const outputsByStep = args.orderedToolSteps.map((toolStep) => {
        const counts = countRoleEntriesByResourceTypeForToolStep({
            toolStep,
            toolMap: args.toolMap,
            direction: 'output',
        });
        counts.forEach((_, resourceTypeHandle) => resourceTypes.add(resourceTypeHandle));
        return counts;
    });

    resourceTypes.forEach((resourceTypeHandle) => {
        maximumArity = Math.max(
            maximumArity,
            inputsByStep[0]?.get(resourceTypeHandle) ?? 0,
            outputsByStep[outputsByStep.length - 1]?.get(resourceTypeHandle) ?? 0,
        );
        for (let stepIndex = 0; stepIndex < args.orderedToolSteps.length - 1; stepIndex += 1) {
            maximumArity = Math.max(
                maximumArity,
                (outputsByStep[stepIndex]?.get(resourceTypeHandle) ?? 0)
                    + (inputsByStep[stepIndex + 1]?.get(resourceTypeHandle) ?? 0),
            );
        }
    });

    return maximumArity;
}

export function deriveWorkflowStateEntryGap(args: {
    toolStepRingGap: number;
    maxArityBetweenToolSteps: number;
}): number {
    if (!Number.isFinite(args.toolStepRingGap) || args.toolStepRingGap < 0) {
        throw new Error('ToolStep ring gap must be finite and non-negative.');
    }
    if (!Number.isInteger(args.maxArityBetweenToolSteps) || args.maxArityBetweenToolSteps < 0) {
        throw new Error('Maximum adjacent workflow state entry arity must be a non-negative integer.');
    }
    return args.toolStepRingGap / (args.maxArityBetweenToolSteps + 2);
}

export function deriveRadialLayoutMetrics(args: {
    resourceTypeRingRadius: number;
    toolStepRingGap: number;
    toolStepCount: number;
    maxArityBetweenToolSteps: number;
}): RadialLayoutMetrics {
    if (!Number.isFinite(args.resourceTypeRingRadius) || args.resourceTypeRingRadius < 0) {
        throw new Error('ResourceType ring radius must be finite and non-negative.');
    }
    if (!Number.isFinite(args.toolStepRingGap) || args.toolStepRingGap <= 0) {
        throw new Error('ToolStep ring gap must be finite and greater than zero.');
    }
    if (!Number.isInteger(args.toolStepCount) || args.toolStepCount < 0) {
        throw new Error('ToolStep count must be a non-negative integer.');
    }
    const toolStepRingGap = args.toolStepRingGap;
    const toolStepRingRadii = Array.from(
        { length: args.toolStepCount },
        (_, index) => args.resourceTypeRingRadius + ((index + 1) * toolStepRingGap),
    );
    return {
        resourceTypeRingRadius: args.resourceTypeRingRadius,
        toolStepConcentricRingOuterRadius:
            args.resourceTypeRingRadius + ((args.toolStepCount + 1) * toolStepRingGap),
        toolStepRingGap,
        toolStepRingRadii,
        maxArityBetweenToolSteps: args.maxArityBetweenToolSteps,
        workflowStateEntryGap: deriveWorkflowStateEntryGap({
            toolStepRingGap,
            maxArityBetweenToolSteps: args.maxArityBetweenToolSteps,
        }),
    };
}

export function countRoleEntriesByResourceType(args: {
    roleByName: Record<string, unknown>;
}): Map<ResourceTypeHandle, number> {
    const out = new Map<ResourceTypeHandle, number>();

    for (const role of Object.values(args.roleByName)) {
        const resourceTypeHandle = extractResourceTypeHandleFromRole(
            role as Parameters<typeof extractResourceTypeHandleFromRole>[0]
        );
        if (!resourceTypeHandle) continue;
        out.set(resourceTypeHandle, (out.get(resourceTypeHandle) ?? 0) + 1);
    }

    return out;
}

export function deriveStepSpokeExtents(args: {
    orderedToolSteps: MutableToolStep[];
    toolMap: ReadonlyMap<ToolHandle, Tool>;
    workflowStateEntriesConfig: WorkflowStateEntriesConfig;
}): StepSpokeExtents {
    return {
        inputByStepIndex: args.orderedToolSteps.map(() => 0),
        outputByStepIndex: args.orderedToolSteps.map(() => 0),
    };
}

export function deriveAdaptiveRingRadii(args: {
    orderedToolSteps: MutableToolStep[];
    toolMap: ReadonlyMap<ToolHandle, Tool>;
    startRadius: number;
    defaultRingGap: number;
    workflowStateEntriesConfig: WorkflowStateEntriesConfig;
    resourceTypeInfo: ResourceTypeAngularInfo;
    directionalExtentScale?: number;
    perpendicularRadius?: number;
    minimumInnerBoundaryRadius?: number;
}): ToolStepRingLayout {
    const {
        orderedToolSteps,
        toolMap,
        startRadius,
        defaultRingGap,
        workflowStateEntriesConfig,
    } = args;
    const directionalExtentScale = args.directionalExtentScale ?? 1;
    if (!Number.isFinite(directionalExtentScale) || directionalExtentScale <= 0) {
        throw new Error('ToolStep directional extent scale must be finite and greater than zero.');
    }

    if (orderedToolSteps.length === 0) {
        return {
            startRadius,
            ringGap: defaultRingGap,
            ringRadii: [],
        };
    }
    if (!workflowStateEntriesConfig.enabled) {
        return {
            startRadius,
            ringGap: defaultRingGap,
            ringRadii: orderedToolSteps.map((_, index) => startRadius + index * defaultRingGap),
        };
    }

    const extents = deriveStepSpokeExtents({
        orderedToolSteps,
        toolMap,
        workflowStateEntriesConfig,
    });

    return deriveUniformToolStepRingLayout({
        stepCount: orderedToolSteps.length,
        startRadius,
        minimumRingGap: defaultRingGap,
        ...(args.minimumInnerBoundaryRadius === undefined
            ? {}
            : { minimumInnerBoundaryRadius: args.minimumInnerBoundaryRadius }),
        envelopeByStepIndex: orderedToolSteps.map((_, index) => ({
            inward: (extents.inputByStepIndex[index] ?? 0) * directionalExtentScale,
            outward: (extents.outputByStepIndex[index] ?? 0) * directionalExtentScale,
            ...(args.perpendicularRadius === undefined
                ? {}
                : { perpendicularRadius: args.perpendicularRadius }),
        })),
    });

}

export function deriveToolStepTubeRadii(args: {
    orderedToolSteps: MutableToolStep[];
    traceSnapshot: ToolStepSlotSnapshot | undefined;
    baseTubeRadius: number;
    tubeRadiusMultipliersByToolStepSlot: {
        topLevel: number;
        what: number;
        when: number;
    };
}): number[] {
    const {
        orderedToolSteps,
        traceSnapshot,
        baseTubeRadius,
        tubeRadiusMultipliersByToolStepSlot,
    } = args;

    const loopWhatToolStepKeys = traceSnapshot?.loopWhatToolStepKeys;
    const loopWhenToolStepKeys = traceSnapshot?.loopWhenToolStepKeys;
    const branchWhatToolStepKeys = traceSnapshot?.branchWhatToolStepKeys;
    const branchWhenToolStepKeys = traceSnapshot?.branchWhenToolStepKeys;

    const resolveToolStepSlot = (stepKey: MutableToolStepKey): 'topLevel' | 'what' | 'when' => {
        const isWhen = loopWhenToolStepKeys?.has(stepKey) || branchWhenToolStepKeys?.has(stepKey);
        if (isWhen) return 'when';

        const isWhat = loopWhatToolStepKeys?.has(stepKey) || branchWhatToolStepKeys?.has(stepKey);
        if (isWhat) return 'what';

        return 'topLevel';
    };

    return orderedToolSteps.map((toolStep) => {
        const stepKey = toolStep.stepKey as MutableToolStepKey;
        const slot = resolveToolStepSlot(stepKey);
        const multiplier = tubeRadiusMultipliersByToolStepSlot[slot];

        return Math.max(0.0001, baseTubeRadius * multiplier);
    });
}